import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Tarification from './pages/Tarification';
import Methodologie from './pages/Methodologie';
import MonCompte from './pages/MonCompte';
import ComprehensionEcrite from './pages/ComprehensionEcrite';
import ComprehensionOrale from './pages/ComprehensionOrale';
import ComprehensionOraleExercise from './pages/ComprehensionOraleExercise';
import ComprehensionEcriteExercise from './pages/ComprehensionEcriteExercise';
import ExpressionEcrite from './pages/ExpressionEcrite';
import ExpressionOrale from './pages/ExpressionOrale';
import ExpressionOraleExercise from './pages/ExpressionOraleExercise';
import ExpressionEcriteExercise from './pages/ExpressionEcriteExercise';
import ExerciseResults from './pages/ExerciseResults';
import ExpressionEcriteResults from './pages/ExpressionEcriteResults';
import ExpressionOraleResults from './pages/ExpressionOraleResults';
import AdminControl from './pages/AdminControl';
import SuperAdmin from './pages/SuperAdmin';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin' && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (superAdminOnly && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const location = useLocation();
  
  // Check for OAuth callback with session_id in hash - handle BEFORE normal routing
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/tarification" element={<Tarification />} />
      <Route path="/methodologie" element={<Methodologie />} />
      <Route
        path="/mon-compte"
        element={
          <ProtectedRoute>
            <MonCompte />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/comprehension-ecrite"
        element={
          <ProtectedRoute>
            <ComprehensionEcrite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/comprehension-orale"
        element={
          <ProtectedRoute>
            <ComprehensionOrale />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/comprehension-orale/exercise"
        element={
          <ProtectedRoute>
            <ComprehensionOraleExercise />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/comprehension-orale/results"
        element={
          <ProtectedRoute>
            <ExerciseResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/comprehension-ecrite/exercise"
        element={
          <ProtectedRoute>
            <ComprehensionEcriteExercise />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/comprehension-ecrite/results"
        element={
          <ProtectedRoute>
            <ExerciseResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/expression-ecrite"
        element={
          <ProtectedRoute>
            <ExpressionEcrite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/expression-ecrite/exercise"
        element={
          <ProtectedRoute>
            <ExpressionEcriteExercise />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/expression-ecrite/results"
        element={
          <ProtectedRoute>
            <ExpressionEcriteResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/expression-orale"
        element={
          <ProtectedRoute>
            <ExpressionOrale />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/expression-orale/exercise"
        element={
          <ProtectedRoute>
            <ExpressionOraleExercise />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/expression-orale/results"
        element={
          <ProtectedRoute>
            <ExpressionOraleResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-control"
        element={
          <ProtectedRoute adminOnly={true}>
            <AdminControl />
          </ProtectedRoute>
        }
      />
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute superAdminOnly={true}>
            <SuperAdmin />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const block = (e) => e.preventDefault();
    document.addEventListener('contextmenu', block);
    return () => document.removeEventListener('contextmenu', block);
  }, []);

  return (
    <BrowserRouter>
      <PayPalScriptProvider options={{ 
        "client-id": "test", // Will be overridden or we can load from settings
        currency: "USD",
        intent: "capture"
      }}>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" />
          </AuthProvider>
        </ThemeProvider>
      </PayPalScriptProvider>
    </BrowserRouter>
  );
}

export default App;
